import React, {
	createContext,
	type ReactNode,
	useCallback,
	useContext,
	useMemo,
	useRef,
} from 'react';

import { fg } from '@atlaskit/platform-feature-flags/fg';

export type PerceivedLatencyAttributes = {
	timeToFirstClickMs?: number;
};

type QuickFindPerceivedLatency = {
	recordQueryChange: (eventTimestamp: number, isClear: boolean) => void;
	recordResultSelected: (eventTimestamp: number) => PerceivedLatencyAttributes;
	resetOnClear: () => void;
};

const NOOP_CONTEXT: QuickFindPerceivedLatency = {
	recordQueryChange: () => {},
	recordResultSelected: () => ({}),
	resetOnClear: () => {},
};

const QuickFindPerceivedLatencyContext = createContext<QuickFindPerceivedLatency>(NOOP_CONTEXT);

const durationBetween = (startedAt: number, endedAt: number): number =>
	Math.max(0, Math.round(endedAt - startedAt));

type FirstClickState =
	| { status: 'idle' }
	| { status: 'pending'; startedAt: number }
	| { status: 'handled' };

const EnabledQuickFindPerceivedLatencyProvider = ({
	children,
}: {
	children: ReactNode;
}): React.JSX.Element => {
	const firstClickStateRef = useRef<FirstClickState>({ status: 'idle' });

	const resetOnClear = useCallback((): void => {
		firstClickStateRef.current = { status: 'idle' };
	}, []);

	const recordQueryChange = useCallback(
		(eventTimestamp: number, isClear: boolean): void => {
			if (isClear) {
				resetOnClear();
				return;
			}

			if (firstClickStateRef.current.status === 'idle') {
				firstClickStateRef.current = { status: 'pending', startedAt: eventTimestamp };
			}
		},
		[resetOnClear],
	);

	const recordResultSelected = useCallback((eventTimestamp: number): PerceivedLatencyAttributes => {
		const firstClickState = firstClickStateRef.current;
		const shouldMeasure = firstClickState.status === 'pending';
		firstClickStateRef.current = { status: 'handled' };

		return shouldMeasure
			? { timeToFirstClickMs: durationBetween(firstClickState.startedAt, eventTimestamp) }
			: {};
	}, []);

	const value = useMemo(
		() => ({
			recordQueryChange,
			recordResultSelected,
			resetOnClear,
		}),
		[recordQueryChange, recordResultSelected, resetOnClear],
	);

	return (
		<QuickFindPerceivedLatencyContext.Provider value={value}>
			{children}
		</QuickFindPerceivedLatencyContext.Provider>
	);
};

export const QuickFindPerceivedLatencyProvider = ({
	children,
}: {
	children: ReactNode;
}): React.JSX.Element => {
	if (!fg('qf_perceived_latency_instrumentation')) {
		return <>{children}</>;
	}

	return (
		<EnabledQuickFindPerceivedLatencyProvider>{children}</EnabledQuickFindPerceivedLatencyProvider>
	);
};

export const useQuickFindPerceivedLatency = (): QuickFindPerceivedLatency =>
	useContext(QuickFindPerceivedLatencyContext);
